import InputAdornment from '@material-ui/core/InputAdornment';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { color } from '../common/themes';
import EmojiPicker from './EmojiPicker';
import TextField from './TextField';
import VoiceInput from './VoiceInput';

import IconSend from './IconSend';

import AutoConnectBot from './AutoConnectBot';
import UploadImage from './UploadImage';
import AutocompleteMenu from './AutocompleteMenu';
import { useAutocomplete } from './hooks/useAutocomplete';

const useStyles = makeStyles(
	() => ({
		root: {
			display: 'flex',
			padding: '13px 10px 13px 10px',
			borderTop: '1px solid rgba(0, 0, 0, 0.1)',
			border: `1px solid ${color.$8}`,
			borderBottomLeftRadius: '12px',
			borderBottomRightRadius: '12px',
			gap: 10,
		},
		endAdornment: {
			paddingTop: '4px',
			paddingRight: '15px',
		},
		fileGroup: {
			display: 'flex',
			alignItems: 'center',
		},
		fileGroup2: {
			display: 'flex',
			alignItems: 'center',
		},
		fileStyle: {
			paddingTop: 4,
		},
		input: {
			'& > div > textarea': {
				paddingRight: 16,
			},
		},
	}),
	{
		name: 'ChatInput',
	}
);

const ChatInput = ({
	styleData,
	botSetting,
	sendBotMessage,
	submitMessage,
	addMessage,
	sendMessage,
	className,
	replyLocale,
	canSend = true,
}: any) => {
	const chartInputRef: any = useRef(null);
	const [chatInput, setChatInput] = useState('');
	const classes = useStyles();
	const { t } = useTranslation();

	const orgId = useSelector((state: any) => state.socketio.orgId);

	// 從 styleData 中提取 showAutocompleteMenu，使用與 showSpeaker 相同的方式
	const showAutocompleteMenu = !!styleData?.showAutocompleteMenu;

	// 條件初始化 useAutocomplete，只有當 showAutocompleteMenu 為 true 時才初始化
	const autocomplete = useAutocomplete(showAutocompleteMenu ? { orgId: orgId || '' } : { orgId: '' });

	const handleVoiceChange = (voiceText: string) => {
		const input = chartInputRef.current;
		if (input && typeof input.selectionStart === 'number' && typeof input.selectionEnd === 'number') {
			const startPos = input.selectionStart;
			const endPos = input.selectionEnd;
			const newValue = input.value.substring(0, startPos) + voiceText + input.value.substring(endPos, input.value.length);
			setChatInput(newValue);
			setTimeout(() => {
				input.focus();
				input.selectionStart = startPos + voiceText.length;
				input.selectionEnd = startPos + voiceText.length;
			});
		} else {
			setChatInput((prev: string) => prev + voiceText);
		}
	};

	const textChangeHandler = (event: any) => {
		const { value } = event.target;
		setChatInput(value);
		chartInputRef.current.focus();
		if (showAutocompleteMenu) {
			autocomplete.handleInputChange(value);
		}
	};

	const onEmojiClick = (emoji: any) => {
		const startPos = chartInputRef.current.selectionStart;
		const endPos = chartInputRef.current.selectionEnd;

		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const newValue =
			chartInputRef.current.value.substring(0, startPos) +
			emoji +
			chartInputRef.current.value.substring(endPos, chartInputRef.current.value.length);

		setChatInput(newValue);

		setTimeout(() => {
			chartInputRef.current.focus();
			chartInputRef.current.selectionStart = startPos + 2;
			chartInputRef.current.selectionEnd = endPos + 2;
		});
	};

	const submitHandler = () => {
		if (!canSend || !chatInput) {
			return;
		}
		submitMessage({ content: chatInput });
		setChatInput('');
		if (showAutocompleteMenu) {
			autocomplete.handleClose();
		}
	};

	const handleSuggestionSelect = (keyword: string) => {
		console.log('[Autocomplete] Suggestion selected:', keyword);
		setChatInput(keyword);
		autocomplete.handleClose();
		chartInputRef.current?.focus();
	};

	return (
		<div className={classnames(classes.root, className)}>
			{!styleData?.hideImageUpload && (
				<div className={classes.fileGroup}>
					<div className={classes.fileStyle} style={{ cursor: canSend ? 'pointer' : 'default', opacity: canSend ? 1 : 0.5 }}>
						<UploadImage sendMessage={sendMessage} addMessage={addMessage} disabled={!canSend} />
					</div>
				</div>
			)}
			{!!botSetting?.status && (
				<div className={classes.fileGroup2}>
					<div
						className={classes.fileStyle}
						style={{ cursor: chatInput && canSend ? 'pointer' : 'default', opacity: canSend ? 1 : 0.5 }}
					>
						<AutoConnectBot sendMessage={sendMessage} sendBotMessage={sendBotMessage} disabled={!canSend} />
					</div>
				</div>
			)}
			<div style={{ position: 'relative', flex: 1 }}>
				<TextField
					inputRef={(ref: any) => {
						chartInputRef.current = ref;
						if (showAutocompleteMenu) {
							autocomplete.setAnchorRef(ref);
						}
					}}
					className={classes.input}
					isValid
					multiline
					minRows={1}
					maxRows={4}
					onChange={textChangeHandler}
					keyPress={(e: any) => {
						if (showAutocompleteMenu) {
							autocomplete.handleInputKeyDown(e);
						}
						if (e.key === 'Enter' && e.shiftKey) {
							// TODO: do nothing no remove this line
						} else if (e.key === 'Enter' && chatInput !== '' && canSend && (showAutocompleteMenu ? !autocomplete.isOpen : true)) {
							submitHandler();
							e.preventDefault();
						} else if (e.key === 'Enter') {
							e.preventDefault();
						}
					}}
					value={chatInput}
					placeholder={t('enterPlaceholder')}
					type="text"
					InputProps={{
						endAdornment: (
							<InputAdornment position="end" className={classes.endAdornment}>
								{styleData?.supportVoiceRecognition && (
									<VoiceInput
										onChange={handleVoiceChange}
										replyLocale={replyLocale}
										style={{ marginRight: 8, cursor: 'pointer' }}
									/>
								)}
								<EmojiPicker onClick={onEmojiClick} />
							</InputAdornment>
						),
					}}
					onFocus={() => {}}
				/>
				<AutocompleteMenu
					open={autocomplete.isOpen}
					suggestions={autocomplete.suggestions}
					focusedIndex={autocomplete.focusedIndex}
					inputValue={chatInput}
					onSelect={handleSuggestionSelect}
					onClose={autocomplete.handleClose}
					onFocusedChange={autocomplete.setFocusedIndex}
				/>
			</div>
			<div className={classes.fileGroup}>
				<div
					className={classes.fileStyle}
					style={{ cursor: chatInput && canSend ? 'pointer' : 'default', opacity: canSend ? 1 : 0.5 }}
				>
					<IconSend id="send" disabled={!chatInput || !canSend} onClick={submitHandler} />
				</div>
			</div>
		</div>
	);
};

export default ChatInput;
